import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/KStackAuthContext';
import { bolusnotesApi } from '../services/api';
import { subscribeSse } from '../services/sse';
import { diag } from '../services/diagnostics';
import {
  DEXCOM_GAP_MS,
  formatNextDue,
  formatTimeAgo,
  getGlucoseColorClass,
  getTrendArrow,
  isReadingStale,
} from '../utils/glucoseDisplay';
import ThemeToggle from '../components/ThemeToggle';
import Avatar from '../components/Avatar';
import AssistantPanel from '../components/AssistantPanel';
import './Dashboard.css';

interface GlucoseReading {
  glucose_value: number;
  trend: string;
  timestamp: string;
}

interface ProfileMetrics {
  id: string;
  name: string;
  profile_image_url?: string;
  latest_reading?: GlucoseReading | null;
}

const Dashboard: React.FC = () => {
  const { user, signOut } = useAuth();
  const [assistantOpen, setAssistantOpen] = useState(false); // BOLUS_NOTES-14
  // BOLUS_NOTES-27: sign out revokes the session on Nexus, clears local state
  // and returns to the login screen.
  const handleSignOut = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };
  const [profiles, setProfiles] = useState<ProfileMetrics[]>([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(Date.now());
  const navigate = useNavigate();

  const profilesRef = useRef(profiles);
  const lastFetchRef = useRef(Date.now());

  useEffect(() => {
    profilesRef.current = profiles;
  }, [profiles]);

  useEffect(() => {
    fetchDashboardData(false);

    // ── SSE: real-time glucose updates ──────────────────────────────
    // Phase F4 (2026-05-25): the dexcom_poll handler emits a `reading`
    // event on the `glucose_update` SSE stream every time it inserts
    // new readings. Subscribing here cuts UI lag from ~3 min (the
    // 120s polling fallback below) to ~1s after the server poll lands.
    //
    // Privacy: the SSE stream is OPEN (no auth filter on the server
    // — see core/streams/types.ts: "Phase 2b-v: open subscription").
    // We safeguard at the fetch layer instead: every event triggers
    // fetchDashboardData(true), which calls get_dashboard_data, and
    // that handler filters by ctx.kstackUserId so we only refetch our
    // own profiles. Worst-case extra cost: a harmless 200 for a
    // sibling family's poll tick.
    //
    // GATEWAY_URL mirrors the default in services/api.ts.
    const GATEWAY_URL =
      process.env.REACT_APP_GATEWAY_URL || 'https://nexus-dev.kstack.app';
    // BOLUS_NOTES-6: the stream is private (bearer + per-user audience),
    // so we use the fetch-based reader in services/sse.ts instead of
    // EventSource (which cannot send an Authorization header).
    const stopSse = subscribeSse(
      `${GATEWAY_URL}/v1/app/bolus_notes/stream/glucose_update`,
      {
        onOpen: () => diag.log('stream', 'connected'),
        onEvent: (name, data) => {
          if (name !== 'reading') return;
          diag.log('stream', 'reading', { size: data.length });
          // Silent refetch — don't toggle the loading spinner mid-session.
          lastFetchRef.current = Date.now();
          fetchDashboardData(true);
        },
        onError: (e) => {
          diag.log('stream', 'error', { message: String((e as any)?.message || e) }, 'warn');
          console.warn('glucose_update SSE error (will reconnect):', e);
        },
      },
    );

    // ── Polling fallback ────────────────────────────────────────────
    // Safety net: catches the first paint after a wake-from-sleep
    // (EventSource may take a few seconds to redial) and covers any
    // case where SSE is unavailable (proxy strips it, etc.).
    const uiTicker = setInterval(() => {
      const currentNow = Date.now();
      setNow(currentNow);

      let anyStale = false;
      profilesRef.current.forEach(p => {
        if (p.latest_reading?.timestamp) {
          const age = currentNow - new Date(p.latest_reading.timestamp).getTime();
          if (age > DEXCOM_GAP_MS) anyStale = true;
        }
      });

      const timeSinceFetch = currentNow - lastFetchRef.current;
      const pollInterval = anyStale ? 3000 : 120000;

      if (timeSinceFetch > pollInterval) {
        lastFetchRef.current = currentNow;
        diag.log('poll', 'tick', { reason: anyStale ? 'stale' : 'interval', sinceFetchMs: timeSinceFetch });
        fetchDashboardData(true);
      }
    }, 1000);
    void diag.refreshFlag();

    return () => {
      clearInterval(uiTicker);
      stopSse();
    };
  }, []);

  const fetchDashboardData = async (isSilent = false) => {
    try {
      if (!isSilent) setLoading(true);
      const data = await bolusnotesApi.dashboard.get();
      setProfiles(data.profiles || []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      if (!isSilent) setLoading(false);
    }
  };

  return (
    <div className="ios-dashboard-container">
      <header className="ios-header">
        <div className="header-greeting">
          <p className="greeting-time">Good evening</p>
          <h1 className="greeting-name">{user?.given_name || user?.username || 'User'}</h1>
          <p className="greeting-email">({user?.email})</p>
        </div>
        <div className="header-actions">
          <ThemeToggle placement="header" />
          <button className="icon-btn" onClick={() => navigate('/settings')}>⚙️</button>
          <button className="icon-btn" onClick={() => navigate('/users')}>👤</button>
          <button className="icon-btn" onClick={() => navigate('/analytics')}>📈</button>
          <button className="icon-btn" title="Assistant" aria-label="Assistant" onClick={() => setAssistantOpen(true)}>💬</button>
          <button className="icon-btn highlight" onClick={() => navigate('/bolus-entries')}>➕</button>
          <button className="icon-btn" title="Sign out" aria-label="Sign out" onClick={handleSignOut}>⏻</button>
        </div>
      </header>

      <div className="status-bar">
        <span className="status-pill online">📶 Online</span>
        <span className="status-pill synced">✅ Synced</span>
      </div>

      <main className="ios-content">
        {loading ? (
          <div className="loading-state">Loading metrics...</div>
        ) : profiles.length === 0 ? (
          <div className="empty-state" onClick={() => navigate('/users')}>
            <p>No active users found.</p>
            <button className="btn-setup">Setup Users</button>
          </div>
        ) : (
          <div className="profiles-list">
            {profiles.map(p => (
              <div className="profile-card" key={p.id} onClick={() => navigate(`/analytics?profile=${p.id}`)}>
                <div className="profile-avatar">
                   <Avatar profileImageUrl={p.profile_image_url} size={60} />
                </div>
                <div className="profile-info">
                  <h3>{p.name}</h3>
                  <div className="glucose-reading">
                    <span className={`glucose-value ${isReadingStale(now, p.latest_reading?.timestamp) ? 'stale' : getGlucoseColorClass(p.latest_reading?.glucose_value)}`}
                          title={isReadingStale(now, p.latest_reading?.timestamp) ? 'Stale — no reading for over 10 minutes' : undefined}>
                      {p.latest_reading?.glucose_value || '--'}
                    </span>
                    <span className="glucose-unit">
                      mg/dL<br/>
                      <span className={`trend-arrow ${isReadingStale(now, p.latest_reading?.timestamp) ? 'stale' : getGlucoseColorClass(p.latest_reading?.glucose_value)}`}>
                        {getTrendArrow(p.latest_reading?.trend)}
                      </span>
                    </span>
                  </div>
                </div>
                <div className="profile-timeline">
                  <p>Last: {formatTimeAgo(now, p.latest_reading?.timestamp)}</p>
                  <p className="next-due">Next: {formatNextDue(now, p.latest_reading?.timestamp)}</p>
                </div>
                <div className="chevron">›</div>
              </div>
            ))}
          </div>
        )}

        <div className="recent-notebooks">
          <h2>Recent Notebooks</h2>
          <div className="notebooks-list">
            {profiles.map(p => (
              <div className="notebook-icon-container" key={p.id} onClick={() => navigate(`/notebooks/${p.id}`)}>
                <div className="notebook-circle">📖</div>
                <span>{p.name}</span>
              </div>
            ))}
          </div>
        </div>
      </main>
      <AssistantPanel open={assistantOpen} onClose={() => setAssistantOpen(false)} />
    </div>
  );
};

export default Dashboard;
